/**
 * ELIA — Logo concepts library (12 directions)
 *
 * Each component takes:
 *   size (default 40)
 *   color (default currentColor for foreground; palette is passed as prop or CSS var)
 *
 * Design intent — every concept must communicate one or more of:
 *   • Intelligence (subtle, never overt)
 *   • Guidance (an arc, a direction)
 *   • Trajectory (life motion, growth)
 *   • Trust (geometric stability)
 *   • Human accompaniment (softness, roundness)
 *
 * Anti-patterns explicitly avoided: brain, lightbulb, robot, neural network.
 * All concepts are favicon-safe (16×16 legibility tested).
 */

const NAVY = "#0B2545";
const GOLD = "#B98A47";

/* ---------- WORDMARKS ---------- */

// 1. Editorial serif lowercase — timeless, luxurious, restrained
export function EliaWordmarkSerif({ size = 40, color = NAVY, accent = GOLD }) {
  const h = size, w = size * 2.4;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} xmlns="http://www.w3.org/2000/svg">
      <text x="0" y={h * 0.78} fontFamily="Fraunces, Georgia, serif" fontWeight="500"
            fontSize={h * 0.58} letterSpacing="-1" fill={color}>altiora</text>
      <circle cx={w - h * 0.15} cy={h * 0.78} r={h * 0.06} fill={accent} />
    </svg>
  );
}

// 2. Modernist sans caps — technology-grade, confidence
export function EliaWordmarkModern({ size = 40, color = NAVY }) {
  const h = size, w = size * 3;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} xmlns="http://www.w3.org/2000/svg">
      <text x="0" y={h * 0.72} fontFamily="Inter, ui-sans-serif" fontWeight="600"
            fontSize={h * 0.52} letterSpacing="4" fill={color}>ALTIORA</text>
    </svg>
  );
}

// 3. Custom low-x-height sans, uniform stroke — quiet, calm
export function EliaWordmarkQuiet({ size = 40, color = NAVY }) {
  const h = size, w = size * 2.6;
  const stroke = h * 0.09;
  const y = h * 0.65;
  const gap = h * 0.55;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} xmlns="http://www.w3.org/2000/svg" fill="none"
         stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
      {/* e */}
      <circle cx={h * 0.32} cy={y} r={h * 0.28} />
      <line x1={h * 0.04} y1={y} x2={h * 0.60} y2={y} />
      {/* l */}
      <line x1={h * 0.85} y1={h * 0.15} x2={h * 0.85} y2={h * 0.95} />
      {/* i */}
      <line x1={h * 1.15} y1={y - h * 0.15} x2={h * 1.15} y2={h * 0.95} />
      <circle cx={h * 1.15} cy={h * 0.22} r={stroke * 0.6} fill={color} />
      {/* a */}
      <circle cx={h * 1.60} cy={y} r={h * 0.28} />
      <line x1={h * 1.88} y1={y - h * 0.25} x2={h * 1.88} y2={h * 0.95} />
    </svg>
  );
}

// 4. Wordmark with ascending dot — trajectory of the "i" transformed
export function EliaWordmarkAscent({ size = 40, color = NAVY, accent = GOLD }) {
  const h = size, w = size * 2.5;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} xmlns="http://www.w3.org/2000/svg">
      <text x="0" y={h * 0.75} fontFamily="Inter, ui-sans-serif" fontWeight="500"
            fontSize={h * 0.75} letterSpacing="-1" fill={color}>el
        <tspan fill={color} fontWeight="500">i</tspan>a
      </text>
      {/* replacement dot for "i" — ascending */}
      <circle cx={h * 1.31} cy={h * 0.10} r={h * 0.09} fill={accent} />
    </svg>
  );
}

/* ---------- MONOGRAMS / SYMBOLS (favicon-first) ---------- */

// 5. E with guidance arc — a subtle arc replaces the middle bar of E
export function EliaSymbolArc({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size, cx = s / 2;
  return (
    <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s} xmlns="http://www.w3.org/2000/svg" fill="none"
         stroke={color} strokeWidth={s * 0.11} strokeLinecap="round">
      <path d={`M ${s * 0.75} ${s * 0.20} L ${s * 0.20} ${s * 0.20} L ${s * 0.20} ${s * 0.80} L ${s * 0.75} ${s * 0.80}`} />
      <path d={`M ${s * 0.32} ${s * 0.50} Q ${s * 0.50} ${s * 0.36} ${s * 0.68} ${s * 0.50}`} stroke={accent} />
    </svg>
  );
}

// 6. Circle with rising path — the trajectory dot inside a whole
export function EliaSymbolOrbit({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size;
  return (
    <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s} xmlns="http://www.w3.org/2000/svg" fill="none">
      <circle cx={s / 2} cy={s / 2} r={s * 0.38} stroke={color} strokeWidth={s * 0.06} />
      <path d={`M ${s * 0.20} ${s * 0.70} Q ${s * 0.50} ${s * 0.30} ${s * 0.80} ${s * 0.25}`}
            stroke={accent} strokeWidth={s * 0.08} strokeLinecap="round" />
      <circle cx={s * 0.80} cy={s * 0.25} r={s * 0.06} fill={accent} />
    </svg>
  );
}

// 7. Constellation — 4 life waypoints connected
export function EliaSymbolConstellation({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size;
  const pts = [
    [0.18, 0.72], [0.38, 0.42], [0.62, 0.55], [0.82, 0.22],
  ];
  return (
    <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s} xmlns="http://www.w3.org/2000/svg" fill="none">
      <path d={pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x * s} ${y * s}`).join(" ")}
            stroke={color} strokeWidth={s * 0.05} strokeLinecap="round" opacity="0.55" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x * s} cy={y * s} r={s * 0.075} fill={i === pts.length - 1 ? accent : color} />
      ))}
    </svg>
  );
}

// 8. Aperture — three overlapping arcs (openness, clarity, decision)
export function EliaSymbolAperture({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size, cx = s / 2, cy = s / 2, r = s * 0.34;
  const stroke = s * 0.09;
  return (
    <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s} xmlns="http://www.w3.org/2000/svg" fill="none"
         strokeLinecap="round" strokeWidth={stroke}>
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r * 0.5} ${cy - r * 0.87}`} stroke={color} />
      <path d={`M ${cx + r * 0.5} ${cy - r * 0.87} A ${r} ${r} 0 0 1 ${cx + r * 0.5} ${cy + r * 0.87}`} stroke={color} />
      <path d={`M ${cx + r * 0.5} ${cy + r * 0.87} A ${r} ${r} 0 0 1 ${cx - r} ${cy}`} stroke={accent} />
    </svg>
  );
}

/* ---------- COMBINATIONS ---------- */

// 9. Symbol + wordmark horizontal — corporate signature
export function EliaLockupHorizontal({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size, w = s * 4.4;
  return (
    <svg viewBox={`0 0 ${w} ${s}`} width={w} height={s} xmlns="http://www.w3.org/2000/svg">
      <g transform={`translate(0 0)`}>
        <EliaSymbolArc size={s} color={color} accent={accent} />
      </g>
      <text x={s * 1.15} y={s * 0.68} fontFamily="Fraunces, Georgia, serif" fontWeight="500"
            fontSize={s * 0.5} letterSpacing="-0.5" fill={color}>altiora</text>
    </svg>
  );
}

// 10. Sphere + wordmark — an orb of intelligence
export function EliaLockupOrb({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size, w = s * 3.2;
  return (
    <svg viewBox={`0 0 ${w} ${s}`} width={w} height={s} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="eliaOrb" cx="30%" cy="30%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.9" />
          <stop offset="70%" stopColor={color} />
          <stop offset="100%" stopColor={color} />
        </radialGradient>
      </defs>
      <circle cx={s / 2} cy={s / 2} r={s * 0.36} fill="url(#eliaOrb)" />
      <text x={s * 1.1} y={s * 0.68} fontFamily="Inter, ui-sans-serif" fontWeight="500"
            fontSize={s * 0.42} letterSpacing="4" fill={color}>ALTIORA</text>
    </svg>
  );
}

// 11. Stacked lockup — symbol on top, wordmark below (institutional)
export function EliaLockupStacked({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size, w = s * 2.4, h = s * 1.55;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} xmlns="http://www.w3.org/2000/svg">
      <g transform={`translate(${(w - s) / 2} 0)`}>
        <EliaSymbolConstellation size={s * 0.9} color={color} accent={accent} />
      </g>
      <text x={w / 2} y={h * 0.94} fontFamily="Fraunces, Georgia, serif" fontWeight="500"
            fontSize={s * 0.44} letterSpacing="0" fill={color} textAnchor="middle">altiora</text>
    </svg>
  );
}

// 12. Monogram E as glyph — a single character with intent, unique letterform
export function EliaMonogramE({ size = 40, color = NAVY, accent = GOLD }) {
  const s = size, pad = s * 0.14;
  return (
    <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s} xmlns="http://www.w3.org/2000/svg" fill="none">
      <rect x={pad * 0.5} y={pad * 0.5} width={s - pad} height={s - pad} rx={s * 0.18} fill={color} />
      <path
        d={`M ${s * 0.72} ${s * 0.28} L ${s * 0.32} ${s * 0.28} L ${s * 0.32} ${s * 0.72} L ${s * 0.72} ${s * 0.72}`}
        stroke="white" strokeWidth={s * 0.09} strokeLinecap="round" strokeLinejoin="round"
      />
      <line x1={s * 0.44} y1={s * 0.50} x2={s * 0.62} y2={s * 0.50} stroke={accent} strokeWidth={s * 0.09} strokeLinecap="round" />
    </svg>
  );
}

export const ELIA_CONCEPTS = [
  { id: 1,  key: "wordmark-serif",  name: "Wordmark éditorial (serif) + point doré", family: "Wordmark",   Component: EliaWordmarkSerif,      intent: "Intemporel, luxe restreint, presse & rapport" },
  { id: 2,  key: "wordmark-modern", name: "Wordmark modernist (caps espacées)",       family: "Wordmark",   Component: EliaWordmarkModern,     intent: "Tech-grade, corporate international" },
  { id: 3,  key: "wordmark-quiet",  name: "Wordmark tracé continu (calme)",           family: "Wordmark",   Component: EliaWordmarkQuiet,      intent: "Silencieux, artisanal, humain" },
  { id: 4,  key: "wordmark-ascent", name: "Wordmark point ascendant",                 family: "Wordmark",   Component: EliaWordmarkAscent,     intent: "Le point du i s'élève — trajectoire" },
  { id: 5,  key: "symbol-arc",      name: "Symbole · E à arc guide",                  family: "Symbole",    Component: EliaSymbolArc,          intent: "E dont la barre médiane devient un arc de guidance" },
  { id: 6,  key: "symbol-orbit",    name: "Symbole · orbite ascendante",              family: "Symbole",    Component: EliaSymbolOrbit,        intent: "Cercle englobant + trajectoire montante" },
  { id: 7,  key: "symbol-constel",  name: "Symbole · constellation 4 points",         family: "Symbole",    Component: EliaSymbolConstellation,intent: "Étapes de vie reliées" },
  { id: 8,  key: "symbol-aperture", name: "Symbole · ouverture (trois arcs)",         family: "Symbole",    Component: EliaSymbolAperture,     intent: "Ouverture, clarté, décision" },
  { id: 9,  key: "lockup-horiz",    name: "Lockup horizontal · symbole + serif",      family: "Combinaison",Component: EliaLockupHorizontal,   intent: "Signature institutionnelle" },
  { id: 10, key: "lockup-orb",      name: "Lockup · sphère + wordmark",               family: "Combinaison",Component: EliaLockupOrb,          intent: "Orbe d'intelligence + caps corporate" },
  { id: 11, key: "lockup-stacked",  name: "Lockup empilé · constellation + serif",    family: "Combinaison",Component: EliaLockupStacked,      intent: "Format vertical, cartes de visite, favicon+" },
  { id: 12, key: "monogram-e",      name: "Monogramme E · glyphe uniquevecarcdoré",   family: "Monogramme", Component: EliaMonogramE,          intent: "App icon, favicon, forte identité" },
];
